import * as React from 'react';
import Card, { CardProps } from '@mui/material/Card';
import { styled, SxProps } from '@mui/material/styles';
import { useInView } from 'react-cool-inview';
import { DEFAULT_SHADOW } from 'styles/theme';
import PresentationModeContext from 'routes/Dashboard/components/PresentationModeContext';
import WidgetMenu from './WidgetMenu';
import WidgetContextMenu from './WidgetContextMenu';
import { NestedMenuOptionT } from 'ui/NestedMenu/NestedMenu';
import { INVIEW_DELAY } from 'business/constants';

export type MenuItemT = {
    name: string;
    icon: React.ReactNode;
    action: () => void;
    hasSubMenu?: boolean;
    subMenuItems?: MenuItemT[];
};

type StyCardStyleProps = {
    isTransparent: boolean;
    editMode: boolean;
    shadow: boolean;
};

type StyCardProps = StyCardStyleProps & CardProps;

const StyCard = styled(Card, {
    name: 'StyCard',
    shouldForwardProp: (prop) => {
        const styleOnlyProps: (keyof StyCardProps)[] = [
            'isTransparent',
            'editMode',
            'shadow',
        ];
        return !styleOnlyProps.includes(prop);
    },
})<StyCardProps>(({ isTransparent, editMode, shadow }) => ({
    width: 'inherit',
    height: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: 'none',
    border: 'solid 1px #efefef',
    overflow: 'auto',
    '& .showOnHover': {
        visibility: 'hidden',
        opacity: 0,
        transition: 'visibility 0.2s, opacity 0.2s linear',
    },
    '&:hover .showOnHover': {
        visibility: 'visible',
        opacity: 1,
    },
    ...(isTransparent
        ? { border: 'none', backgroundColor: 'transparent' }
        : {}),
    ...(editMode
        ? {
              cursor: 'grab',
              '&:active': {
                  cursor: 'grabbing',
              },
          }
        : {}),
    ...(shadow ? DEFAULT_SHADOW : {}),
}));

const StyFloatingContainer = styled('div', {
    name: 'StyFloatingContainer',
})({
    position: 'absolute',
    zIndex: 1,
    top: '8px',
    right: '8px',
});

const StyChartHeader = styled('div', {
    name: 'StyChartHeader',
})({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: '41px',
    overflow: 'hidden',
    alignItems: 'center',
    padding: '0 8px 0 1rem',
});

type Props = {
    children: React.ReactNode;
    editMode: boolean;
    canEdit?: boolean;
    transparent?: boolean;
    shadow?: boolean;
    menuItems: NestedMenuOptionT[];
    title?: React.ReactNode;
    exportMenu?: React.ReactNode;
    lazyLoading?: boolean;
    onDoubleClick?: React.MouseEventHandler<HTMLDivElement>;
    disableRightClick?: boolean;
    'data-testid'?: string;
    sx?: SxProps;
};

/** Wrap a widget component in a card containing a menu with some actions (edit, delete ..).
 *
 * @param {boolean} canEdit: true when the user has the right to edit the widget
 * @param {boolean} editMode: true while the user is editing the widget
 * @param {boolean} transparent: if true, does not have background nor border
 */
function WidgetCard({
    children,
    editMode,
    menuItems,
    title = null,
    canEdit = true,
    transparent = false,
    shadow = false,
    exportMenu = null,
    lazyLoading = false,
    onDoubleClick = () => {},
    disableRightClick = false,
    sx = undefined,
    ...props
}: Props): React.ReactElement {
    const [rightClickPosition, setRightClickPosition] = React.useState({
        x: 0,
        y: 0,
    });
    const [openContextMenu, setOpenContextMenu] = React.useState(false);
    const [isInPresentationMode] = React.useContext(PresentationModeContext);
    const onRightClick = (event: React.MouseEvent) => {
        const isTextSelected = Boolean(window.getSelection()?.toString());
        if (disableRightClick || isInPresentationMode || isTextSelected) {
            return;
        }
        event.preventDefault();
        setRightClickPosition({
            x: event.clientX,
            y: event.clientY,
        });
        setOpenContextMenu(true);
    };

    const isMenuFloating = Boolean(!title);
    const menus = (
        <WidgetMenu
            editMode={editMode}
            menuItems={menuItems}
            canEdit={canEdit}
            exportMenu={exportMenu}
        />
    );
    const header = isMenuFloating ? (
        <StyFloatingContainer>{menus}</StyFloatingContainer>
    ) : (
        <StyChartHeader>
            {' '}
            {title} {menus}
        </StyChartHeader>
    );

    const { observe, inView } = useInView({
        unobserveOnEnter: true,
        delay: INVIEW_DELAY,
    });
    const shouldDisplayContent = !lazyLoading || inView;
    return (
        <StyCard
            isTransparent={!!transparent}
            editMode={editMode}
            shadow={!!shadow}
            onContextMenu={onRightClick}
            onDoubleClick={onDoubleClick}
            ref={observe}
            data-testid={props['data-testid']}
            sx={sx}
        >
            {header}
            {shouldDisplayContent && children}
            {openContextMenu && (
                <WidgetContextMenu
                    open={openContextMenu}
                    onClose={() => setOpenContextMenu(false)}
                    menuItems={menuItems}
                    x={rightClickPosition.x}
                    y={rightClickPosition.y}
                />
            )}
        </StyCard>
    );
}

export default WidgetCard;
